import { useEffect, useRef, useState } from 'react';
import { FACE_HEIGHT as H, FACE_WIDTH as W, type PublicFaces } from '@karozik/shared';
import { cn } from './api.js';
import { renderWarped, WARP_MODES, type Landmarks, type WarpMode } from './morph.js';

type FaceMode = NonNullable<PublicFaces['mode']>;

export const FACE_MODE_LABELS: Record<FaceMode, string> = {
  blend: 'Fondu',
  split: 'Découpe',
  stripes: 'Lamelles',
  eyes: 'Échange de regards',
  morph: 'Morphing',
  features: 'Traits échangés',
  caricature: 'Caricature',
};

const STEP_COUNT = 10;

/** Pixel size of each reveal step, from `start` (unrecognisable) down to 3 px (almost sharp). */
export function faceSteps(start: number): number[] {
  return Array.from({ length: STEP_COUNT }, (_, i) => Math.max(3, Math.round(start * (3 / start) ** (i / (STEP_COUNT - 1)))));
}

export function faceStep(elapsedMs: number, durationMs: number): number {
  const p = Math.min(1, Math.max(0, elapsedMs / durationMs));
  return Math.min(STEP_COUNT - 1, Math.floor(p * STEP_COUNT));
}

/** `img` restricted to the horizontal band y0..y1, with soft edges of `feather` px. */
function band(img: CanvasImageSource, y0: number, y1: number, feather: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const x = c.getContext('2d')!;
  x.drawImage(img, 0, 0, W, H);
  x.globalCompositeOperation = 'destination-in';
  const top = y0 - feather;
  const total = y1 + feather - top;
  const g = x.createLinearGradient(0, top, 0, y1 + feather);
  g.addColorStop(0, 'rgba(0,0,0,0)');
  g.addColorStop(Math.min(0.5, (2 * feather) / total), 'rgba(0,0,0,1)');
  g.addColorStop(Math.max(0.5, 1 - (2 * feather) / total), 'rgba(0,0,0,1)');
  g.addColorStop(1, 'rgba(0,0,0,0)');
  x.fillStyle = g;
  x.fillRect(0, 0, W, H);
  return c;
}

/**
 * Draws face `a` mixed with face `b` (both cropped to the same frame, eyes aligned).
 * The warped modes need both faces' landmarks; without them they fall back to a plain blend.
 */
export function mashFaces(
  out: HTMLCanvasElement,
  a: CanvasImageSource,
  b: CanvasImageSource,
  mode: FaceMode,
  extra: { la?: Landmarks | null; lb?: Landmarks | null; ratio?: number; caricature?: number } = {},
) {
  if (WARP_MODES.includes(mode)) {
    if (extra.la && extra.lb) {
      renderWarped(out, a, b, extra.la, extra.lb, mode as WarpMode, { ratio: extra.ratio ?? 0.5, caricature: extra.caricature ?? 1.6 });
      return;
    }
    mode = 'blend';
  }
  const x = out.getContext('2d')!;
  x.save();
  x.clearRect(0, 0, W, H);
  x.drawImage(a, 0, 0, W, H);
  if (mode === 'blend') {
    x.globalAlpha = 0.5;
    x.drawImage(b, 0, 0, W, H);
  } else if (mode === 'split') {
    x.drawImage(band(b, 236, H + 80, 16), 0, 0);
  } else if (mode === 'eyes') {
    x.drawImage(band(b, 150, 226, 13), 0, 0);
  } else {
    const stripe = 36;
    for (let left = stripe; left < W; left += stripe * 2) {
      x.save();
      x.beginPath();
      x.rect(left, 0, stripe, H);
      x.clip();
      x.drawImage(b, 0, 0, W, H);
      x.restore();
    }
  }
  x.restore();
}

const tiny = typeof document !== 'undefined' ? document.createElement('canvas') : null;

export function drawPixelated(ctx: CanvasRenderingContext2D, src: HTMLCanvasElement, block: number) {
  ctx.clearRect(0, 0, W, H);
  if (block <= 1 || !tiny) {
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(src, 0, 0, W, H);
    return;
  }
  const w = Math.ceil(W / block);
  const h = Math.ceil(H / block);
  tiny.width = w;
  tiny.height = h;
  const t = tiny.getContext('2d')!;
  t.imageSmoothingEnabled = true;
  t.imageSmoothingQuality = 'high';
  t.drawImage(src, 0, 0, w, h);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(tiny, 0, 0, w, h, 0, 0, w * block, h * block);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * The round's picture: two selfies mixed, un-pixelating step by step on the
 * server clock so the screen and every phone show the same step.
 */
export function FaceMashup({
  faces,
  durationMs,
  clockSkew,
  sharp,
  className,
}: {
  faces: Pick<PublicFaces, 'images' | 'mode' | 'startedAt' | 'render'>;
  durationMs: number;
  clockSkew: number;
  sharp: boolean;
  className?: string;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState<HTMLCanvasElement | null>(null);
  const [failed, setFailed] = useState(false);
  const { images, mode, startedAt, render } = faces;
  const steps = faceSteps(render.pixelStart);

  useEffect(() => {
    setReady(null);
    setFailed(false);
    if (!images || !mode) return;
    let alive = true;
    const landmarks: Promise<{ a: Landmarks | null; b: Landmarks | null }> = WARP_MODES.includes(mode)
      ? fetch(images.landmarks, { credentials: 'same-origin' })
          .then((r) => (r.ok ? r.json() : { a: null, b: null }))
          .catch(() => ({ a: null, b: null }))
      : Promise.resolve({ a: null, b: null });
    Promise.all([loadImage(images.a), loadImage(images.b), landmarks])
      .then(([a, b, lm]) => {
        if (!alive) return;
        const src = document.createElement('canvas');
        src.width = W;
        src.height = H;
        mashFaces(src, a, b, mode, { la: lm.a, lb: lm.b, ratio: render.ratio, caricature: render.caricature });
        setReady(src);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [images?.a, images?.b, mode, render.ratio, render.caricature]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const ctx = canvas.current?.getContext('2d');
    if (!ctx || !ready) return;
    if (sharp || startedAt == null) {
      drawPixelated(ctx, ready, 1);
      return;
    }
    let raf = 0;
    let last = -1;
    const frame = () => {
      const elapsed = Date.now() + clockSkew - startedAt;
      // Past the reveal time the picture is fully sharp (the last-chance window).
      const step = elapsed >= durationMs ? STEP_COUNT : faceStep(elapsed, durationMs);
      if (step !== last) {
        last = step;
        drawPixelated(ctx, ready, step === STEP_COUNT ? 1 : steps[step]!);
      }
      if (step < STEP_COUNT) raf = requestAnimationFrame(frame);
    };
    frame();
    return () => cancelAnimationFrame(raf);
  }, [ready, sharp, startedAt, durationMs, clockSkew, render.pixelStart]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={cn('relative aspect-[4/5] overflow-hidden bg-surface-2', className)}>
      <canvas ref={canvas} width={W} height={H} className="h-full w-full [image-rendering:pixelated]" role="img" aria-label="Deux visages mélangés" />
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center text-[40%] text-ink-faint">{failed ? '🙈' : '…'}</div>
      )}
    </div>
  );
}
